import React, { createContext, useContext, useState, ReactNode } from 'react';
import { GoogleOAuthConfig } from '../../types';

// SDK 로드 상태 인터페이스
interface SDKLoadState {
  kakao: boolean;
  google: boolean;
  apple: boolean;
}

// SocialAuth 컨텍스트 인터페이스
interface SocialAuthContextType {
  loadKakaoSDK: () => Promise<boolean>;
  loadGoogleSDK: () => Promise<boolean>;
  loadAppleSDK: () => Promise<boolean>;
  isSDKLoaded: (sdk: keyof SDKLoadState) => boolean;
  isLoading: boolean;
}

// 컨텍스트 생성
const SocialAuthContext = createContext<SocialAuthContextType | undefined>(undefined);

interface SocialAuthProviderProps {
  children: ReactNode;
}

const SocialAuthProvider: React.FC<SocialAuthProviderProps> = ({ children }) => {
  const [sdkLoadState, setSdkLoadState] = useState<SDKLoadState>({
    kakao: false,
    google: false,
    apple: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  // SDK 로드 상태 확인
  const isSDKLoaded = (sdk: keyof SDKLoadState): boolean => {
    return sdkLoadState[sdk];
  };

  // Kakao SDK 로드
  const loadKakaoSDK = async (): Promise<boolean> => {
    if (sdkLoadState.kakao || window.Kakao) {
      return true;
    }

    setIsLoading(true);
    try {
      const kakaoScript = document.createElement('script');
      kakaoScript.src = 'https://developers.kakao.com/sdk/js/kakao.js';
      kakaoScript.async = true;
      
      await new Promise<void>((resolve, reject) => {
        kakaoScript.onload = () => resolve();
        kakaoScript.onerror = () => reject(new Error('Kakao SDK 로드 실패'));
        document.head.appendChild(kakaoScript);
      });

      setSdkLoadState(prev => ({ ...prev, kakao: true }));
      // console.log('Kakao SDK 로드 성공');
      return true;
    } catch (error) {
      console.error('Kakao SDK 로드 실패:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Google OAuth SDK 로드
  const loadGoogleSDK = async (): Promise<boolean> => {
    if (sdkLoadState.google || window.google) {
      return true;
    }

    setIsLoading(true);
    try {
        const googleScript = document.createElement('script');
        googleScript.src = 'https://accounts.google.com/gsi/client';
        googleScript.async = true;
        googleScript.defer = true;
        
        await new Promise<void>((resolve, reject) => {
          googleScript.onload = () => resolve();
          googleScript.onerror = () => reject(new Error('Google SDK 로드 실패'));
          document.head.appendChild(googleScript);
        });

        // Google OAuth 초기화
        const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your_google_client_id_here';
        if (window.google && (window.google as any).accounts) {
        // FedCM 지원 여부 확인
        const isFedCMSupported = 'IdentityCredential' in window;
        
        // 기본 설정
        const initializeConfig: GoogleOAuthConfig = {
          client_id: googleClientId,
          callback: (response: any) => {
            console.log('Google OAuth 콜백:', response);
            if ((window as any).handleCredentialResponse) {
              (window as any).handleCredentialResponse(response);
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          context: 'signin' as const
        };
        
        // FedCM 지원 시 추가 설정
        if (isFedCMSupported) {
          initializeConfig.use_fedcm_for_prompt = true;
          initializeConfig.prompt_parent_id = 'google-login-container';
          initializeConfig.state_cookie_domain = window.location.hostname;
          initializeConfig.ux_mode = 'popup';
          initializeConfig.itp_support = true;
        }
        
        try {
          (window.google as any).accounts.id.initialize(initializeConfig);
          console.log('Google OAuth 초기화 성공');
        } catch (error) {
          console.error('Google OAuth 초기화 실패:', error);
          // 기본 설정으로 재시도
          const fallbackConfig: GoogleOAuthConfig = {
            client_id: googleClientId,
            callback: initializeConfig.callback,
            auto_select: false,
            cancel_on_tap_outside: true,
            context: 'signin' as const
          };
          (window.google as any).accounts.id.initialize(fallbackConfig);
          console.log('Google OAuth 기본 설정으로 초기화 성공');
        }
      }

      setSdkLoadState(prev => ({ ...prev, google: true }));
      // console.log('Google SDK 로드 성공');
      return true;
    } catch (error) {
      console.error('Google SDK 로드 실패:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Apple Sign In SDK 로드
  const loadAppleSDK = async (): Promise<boolean> => {
    if (sdkLoadState.apple || window.AppleID) {
      return true;
    }

    setIsLoading(true);
    try {
      const appleScript = document.createElement('script');
      appleScript.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      appleScript.async = true;
      
      await new Promise<void>((resolve, reject) => {
        appleScript.onload = () => resolve();
        appleScript.onerror = () => reject(new Error('Apple SDK 로드 실패'));
        document.head.appendChild(appleScript);
      });

      // Apple Sign In 초기화
      const appleClientId = process.env.REACT_APP_APPLE_CLIENT_ID;
      const appleRedirectUri = process.env.REACT_APP_APPLE_REDIRECT_URI || 'http://localhost:3000/auth/apple/callback';
      
      if (window.AppleID && window.AppleID.auth) {
        window.AppleID.auth.init({
          clientId: appleClientId,
          response_mode: 'form_post',
          response_type: 'code',
          scope: 'name email',
          redirectURI: appleRedirectUri,
          state: 'origin:web'
        });
      }

      setSdkLoadState(prev => ({ ...prev, apple: true }));
      // console.log('Apple SDK 로드 성공');
      return true;
    } catch (error) {
      console.error('Apple SDK 로드 실패:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: SocialAuthContextType = {
    loadKakaoSDK,
    loadGoogleSDK,
    loadAppleSDK,
    isSDKLoaded,
    isLoading,
  };

  return (
    <SocialAuthContext.Provider value={contextValue}>
      {children}
    </SocialAuthContext.Provider>
  );
};

// 커스텀 훅
export const useSocialAuth = (): SocialAuthContextType => {
  const context = useContext(SocialAuthContext);
  if (context === undefined) {
    throw new Error('useSocialAuth는 SocialAuthProvider 내부에서 사용되어야 합니다.');
  }
  return context;
};

export default SocialAuthProvider; 